import { apiService as api } from '@/store/apiService';
import type { GroupAgentSettings } from '../../director-agents/DirectorAgentsApi';
export const addTagTypes = [
  'director_agents',
  'director_agent',
  'settings_account',
  'account_time_zone',
  'workflow_schedules',
  'settings_notification',
  'settings_security',
  'settings_plan_billing',
  'settings_team',
  'settings_team_member',
  'settings_groups',
  'settings_permissions',
  'settings_central_permissions',
  'settings_group_permissions',
  'settings_group_members',
  'settings_subcategory_permissions',
  'settings_users',
  'settings_user_applications',
  'settings_user_permissions',
  'workspace_runtime',
  'workspace_options',
  'settings_token_usage',
  'settings_token_allowance',
] as const;

// A saved group changes both the editor and each member's effective access.
const groupAccessTags = [
  'settings_groups', 'settings_group_permissions', 'settings_group_members',
  'settings_user_permissions', 'settings_user_applications',
  'workspace_runtime', 'workspace_options', 'director_agents', 'director_agent'
] as const;

const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getAccountTimeZone: build.query<{ timeZoneId: string; useDeviceTimeZone: boolean }, void>({
        query: () => ({ url: '/api/settings/time-zone' }),
        providesTags: ['account_time_zone']
      }),
      getAccountSettings: build.query<GetAccountSettingsApiResponse, void>({
        query: () => ({ url: `/api/settings/account-settings` }),
        providesTags: ['settings_account']
      }),
      updateAccountSettings: build.mutation<UpdateAccountSettingsApiResponse, UpdateAccountSettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/settings/account-settings`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: ['settings_account', 'account_time_zone', 'workflow_schedules']
      }),
      uploadIcon: build.mutation<UploadIconResponse, File>({
        query: (file) => {
          const formData = new FormData();
          formData.append('file', file);
          return {
            url: `/api/settings/upload-icon`,
            method: 'POST',
            body: formData,
            // Don't let RTK Query serialize FormData to JSON
            formData: true
          };
        },
        invalidatesTags: ['settings_account']
      }),
      getTitle: build.query<GetTitleSettingsApiResponse, void>({
        query: () => ({ url: `/api/settings/title` }),
        transformResponse: (response: { title: string }) => response.title,
        providesTags: ['settings_account']
      }),
      getIcon: build.query<string, void>({
        query: () => ({
          url: `/api/settings/icon`,
          responseHandler: async (response) => {
            if (!response.ok) return '';
            const blob = await response.blob();
            if (!blob.size || !blob.type.startsWith('image/')) return '';
            return URL.createObjectURL(blob);
          }
        }),
        providesTags: ['settings_account'],
        // Clean up object URL when the cache entry is removed
        onCacheEntryAdded: async (arg, { cacheDataLoaded, cacheEntryRemoved }) => {
          try {
            const url = await cacheDataLoaded;
            await cacheEntryRemoved;
            URL.revokeObjectURL(url);
          } catch {}
        }
      }),
      getUserApplications: build.query<GetUserApplicationsApiResponse, void>({
        query: () => ({ url: `/api/settings/applications/user` }),
        providesTags: ['settings_user_applications']
      }),

      // User Permissions Endpoints
      getUserPermissions: build.query<GetUserPermissionsApiResponse, void>({
        query: () => ({ url: `/api/settings/user/permissions` }),
        providesTags: ['settings_user_permissions']
      }),

      getGroupedCentralPermissions: build.query<GroupPermissionWithChildren[], void>({
        query: () => ({ url: `/api/settings/permissions/central` }),
        providesTags: ['settings_central_permissions'],
      }),

      getSuperAdminStatus: build.query<GetSuperAdminStatusApiResponse, void>({
        query: () => ({ url: `/api/settings/superadmin-status` }),
        providesTags: ['settings_user_permissions']
      }),

      getTeamMembersSettings: build.query<GetTeamMembersSettingsApiResponse, GetTeamMembersSettingsApiArg>({
        query: () => ({ url: `/api/settings/team-members` }),
        providesTags: ['settings_team']
      }),
      createTeamMemberSettings: build.mutation<CreateTeamMemberSettingsApiResponse, CreateTeamMemberSettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/settings/team-members`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: ['settings_team', 'settings_users']
      }),

      deleteTeamMemberSettings: build.mutation<any, { id: string }>({
        query: (queryArg) => ({
          url: `/api/settings/team-members/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['settings_team', 'settings_team_member', 'settings_users']
      }),
      updateTeamMemberSettings: build.mutation<UpdateTeamMemberSettingsApiResponse, UpdateTeamMemberSettingsApiArg>({
        query: ({ id, ...body }) => ({
          url: `/api/settings/team-members/${id}`,
          method: 'PUT',
          body
        }),
        invalidatesTags: ['settings_team', 'settings_users']
      }),
      
      // ✅ Team Member Password Reset (SuperAdmin only)
      resetTeamMemberPassword: build.mutation<
        { message: string },
        { id: string; newPassword: string; confirmPassword: string }
      >({
        query: ({ id, ...body }) => ({
          url: `/api/settings/team-members/${id}/reset-password`,
          method: 'POST',
          body
        }),
        invalidatesTags: ['settings_team', 'settings_users']
      }),

      getGroupsSettings: build.query<GetGroupsSettingsApiResponse, GetGroupsSettingsApiArg>({
        query: () => ({ url: `/api/settings/groups` }),
        providesTags: ['settings_groups']
      }),

      createGroupsSettings: build.mutation<CreateGroupsSettingsApiResponse, CreateGroupsSettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/settings/groups`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: [...groupAccessTags]
      }),

      createPermission: build.mutation<unknown, GroupPermission>({
        query: (queryArg) => ({
          url: `/api/settings/permissions`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: ['settings_permissions']
      }),

      deleteGroupsSettings: build.mutation<any, { id: string }>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: [...groupAccessTags]
      }),
      updateGroupsSettings: build.mutation<UpdateGroupsSettingsApiResponse, UpdateGroupsSettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.id}`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: [...groupAccessTags]
      }),

      getPermissions: build.query<GroupPermission[], void>({
        query: () => ({ url: `/api/settings/permissions` }),
        providesTags: ['settings_permissions']
      }),

      getGroupedPermissions: build.query<GroupPermissionWithChildren[], void>({
         query: () => ({ url: `/api/settings/permissions/grouped` }),
         providesTags: ['settings_permissions'],
      }),

      // Group Permissions Management
      getGroupPermissions: build.query<GroupPermission[], string>({
        query: (groupId) => ({ url: `/api/settings/groups/${groupId}/permissions` }),
        providesTags: (result, error, groupId) => [{ type: 'settings_group_permissions', id: groupId }]
      }),

      assignGroupPermission: build.mutation<unknown, { groupId: string, permissionIds: string[] }>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/permissions`,
          method: 'POST',
          body: { permissionIds: queryArg.permissionIds }
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_permissions', id: arg.groupId },
          'settings_groups'
        ]
      }),

      removeGroupPermission: build.mutation<unknown, { groupId: string, permissionId: string }>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/permissions/${queryArg.permissionId}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_permissions', id: arg.groupId },
          'settings_groups'
        ]
      }),

      // Group Members Management
      getGroupMembers: build.query<GroupMember[], string>({
        query: (groupId) => ({ url: `/api/settings/groups/${groupId}/members` }),
        providesTags: (result, error, groupId) => [{ type: 'settings_group_members', id: groupId }]
      }),

      addGroupMember: build.mutation<unknown, { groupId: string, memberId: string }>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/members`,
          method: 'POST',
          body: { memberId: queryArg.memberId }
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_members', id: arg.groupId },
          'settings_groups'
        ]
      }),

      removeGroupMember: build.mutation<unknown, { groupId: string, memberId: string }>({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/members/${queryArg.memberId}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_members', id: arg.groupId },
          'settings_groups'
        ]
      }),

      // Subcategory Permissions
      getSubcategoryPermissions: build.query<SubPermission[], void>({
        query: () => ({ url: `/api/settings/subcategory-permissions` }),
        providesTags: ['settings_subcategory_permissions']
      }),

      getPermissionSubcategories: build.query<SubPermission[], string>({
        query: (permissionId) => ({ url: `/api/settings/permissions/${permissionId}/subcategories` }),
        providesTags: (result, error, permissionId) => [
          { type: 'settings_subcategory_permissions', id: permissionId }
        ]
      }),

      createSubcategoryPermission: build.mutation<unknown, SubPermission>({
        query: (queryArg) => ({
          url: `/api/settings/subcategory-permissions`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: ['settings_subcategory_permissions', 'settings_permissions']
      }),

      updateSubcategoryPermission: build.mutation<unknown, SubPermission>({
        query: (queryArg) => ({
          url: `/api/settings/subcategory-permissions`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: ['settings_subcategory_permissions', 'settings_permissions']
      }),

      deleteSubcategoryPermission: build.mutation<unknown, { id: string }>({
        query: (queryArg) => ({
          url: `/api/settings/subcategory-permissions/${queryArg.id}`,
          method: 'DELETE'
        }),
        invalidatesTags: ['settings_subcategory_permissions', 'settings_permissions']
      }),

      // Group Subcategory Permissions
     getGroupSubcategoryPermissions: build.query<SubPermission[], string>({
            query: (groupId) => ({ url: `/api/settings/groups/${groupId}/subcategory-permissions` }),
            providesTags: (result, error, groupId) => [
              { type: 'settings_group_permissions', id: groupId }
            ]
     }),

      assignGroupSubcategoryPermission: build.mutation<
        unknown,
        { groupId: string, subcategoryPermissionIds: string[] }
      >({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/subcategory-permissions`,
          method: 'POST',
          body: { subcategoryPermissionIds: queryArg.subcategoryPermissionIds }
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_permissions', id: arg.groupId },
          'settings_groups',
          'settings_user_permissions'
        ]
      }),

      removeGroupSubcategoryPermission: build.mutation<
        unknown,
        { groupId: string, permissionId: string }
      >({
        query: (queryArg) => ({
          url: `/api/settings/groups/${queryArg.groupId}/subcategory-permissions/${queryArg.permissionId}`,
          method: 'DELETE'
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'settings_group_permissions', id: arg.groupId },
          'settings_groups',
          'settings_user_permissions'
        ]
      }),

      // Users for selecting group members
      getUsers: build.query<User[], void>({
        query: () => ({ url: `/api/settings/users` }),
        providesTags: ['settings_users']
      }),

      getNotificationSettings: build.query<GetNotificationSettingsApiResponse, GetNotificationSettingsApiArg>({
        query: () => ({ url: `/api/mock/app-notification-settings/0` }),
        providesTags: ['settings_notification']
      }),
      updateNotificationSettings: build.mutation<
        UpdateNotificationSettingsApiResponse,
        UpdateNotificationSettingsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/mock/app-notification-settings/0`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: ['settings_notification']
      }),
      
      // ✅ SECURITY SETTINGS - Real Endpoints
      getSecuritySettings: build.query<GetSecuritySettingsApiResponse, GetSecuritySettingsApiArg>({
        query: () => ({ url: `/api/settings/security-settings` }),
        providesTags: ['settings_security']
      }),
      updateSecuritySettings: build.mutation<UpdateSecuritySettingsApiResponse, UpdateSecuritySettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/settings/security-settings`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: ['settings_security']
      }),
      changePassword: build.mutation<
        { message: string }, 
        { currentPassword: string; newPassword: string; confirmPassword: string }
      >({
        query: (queryArg) => ({
          url: `/api/settings/change-password`,
          method: 'POST',
          body: queryArg
        }),
        invalidatesTags: ['settings_security']
      }),
      
      getPlanBillingSettings: build.query<GetPlanBillingSettingsApiResponse, GetPlanBillingSettingsApiArg>({
        query: () => ({ url: `/api/mock/app-plan-billing-settings/0` }),
        providesTags: ['settings_plan_billing']
      }),
      updatePlanBillingSettings: build.mutation<UpdatePlanBillingSettingsApiResponse, UpdatePlanBillingSettingsApiArg>({
        query: (queryArg) => ({
          url: `/api/mock/app-plan-billing-settings/0`,
          method: 'PUT',
          body: queryArg
        }),
        invalidatesTags: ['settings_plan_billing']
      }),

      // ── Token Usage (Ask-My-Archive) — SuperAdmin only ──────────────────────────
      getTokenUsageSummary: build.query<TokenUsageSummary, void>({
        query: () => ({ url: `/api/token-usage/summary` }),
        providesTags: ['settings_token_usage', 'settings_token_allowance']
      }),
      getTokenUsageSeries: build.query<TokenUsageSeries, { days?: number }>({
        query: ({ days = 30 } = {}) => ({ url: `/api/token-usage/series`, params: { days } }),
        providesTags: ['settings_token_usage']
      }),
      getTokenUsageBreakdown: build.query<TokenUsageBreakdown, { by?: 'user' | 'archive' | 'source' | 'model' | 'workflow' | 'batch' | 'request' | 'phase'; days?: number }>({
        query: ({ by = 'user', days = 30 } = {}) => ({ url: `/api/token-usage/breakdown`, params: { by, days } }),
        providesTags: ['settings_token_usage']
      }),
      getTokenAllowance: build.query<TokenAllowance, void>({
        query: () => ({ url: `/api/token-usage/allowance` }),
        providesTags: ['settings_token_allowance']
      }),
      getTokenRequest: build.query<{ messageId: string; rows: TokenRequestRow[] }, string>({
        query: (messageId) => ({ url: `/api/token-usage/requests/${messageId}` }),
        providesTags: ['settings_token_usage']
      }),
      updateTokenAllowance: build.mutation<TokenAllowance, TokenAllowanceUpdate>({
        query: (body) => ({ url: `/api/token-usage/allowance`, method: 'PUT', body }),
        invalidatesTags: ['settings_token_allowance', 'settings_token_usage']
      })
    }),
    overrideExisting: false
  });

export { injectedRtkApi as SettingsApi };

// Original type definitions
export type GetAccountSettingsApiResponse = /** status 200 OK */ SettingsAccount;
export type GetAccountSettingsApiArg = void;
export type UpdateAccountSettingsApiResponse = unknown;
export type UpdateAccountSettingsApiArg = {
  DisplayName?: string; Email?: string; Title?: string; Settings?: string; Shortcuts?: string;
  OpeningPage?: string; TimeZoneId?: string; UseDeviceTimeZone?: boolean;
};

// User Permissions API Types
export type GetUserPermissionsApiResponse = {
    tenantFeatures: { agentsEnabled: boolean };
    permissions: Array<{ id: string; name: string; description: string }>;  // Main permission objects
    subcategoryPermissions: UserPermissions;  // Boolean flags computed from main permissions
    user: UserInfo;
    groups: GroupInfo[];
};

export type GetSuperAdminStatusApiResponse = {
  isSuperAdmin: boolean;
};

export interface UserPermissions {
  hasTrainAI?: boolean;
  hasWorkflowCategory: boolean;
  hasApplicationCategory: boolean;
  hasAdminCategory: boolean;
  hasCaptureCategory: boolean;
  hasAgentsCategory: boolean;
  hasCreateForm: boolean;
  hasApproveForm: boolean;
  hasIndexBatches: boolean;
  hasScanInCapture: boolean;
  hasScanInArchive: boolean;
  hasDownloadInArchive: boolean;
  hasPrintInArchive: boolean;
  hasDeleteDocuments: boolean;
  hasModifyDocuments: boolean;
  hasCreateApplications: boolean;
  hasAddWorkflows: boolean;
  hasDeleteWorkflows: boolean;
  hasAddEmployee: boolean;
  hasRemoveEmployee: boolean;
  hasAddDeleteGroups: boolean;
  hasViewAudit: boolean;
  hasViewOnly: boolean;
  hasAnyBatchPermissions: boolean;
  hasAnyArchivePermissions: boolean;
  hasAnyAdminPermissions: boolean;
}

export interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  isSuperAdmin: boolean;
  role?: string;
}

export interface GroupInfo {
  id: string;
  name: string;
}

export type GetNotificationSettingsApiResponse = /** status 200 OK */ SettingsNotifications;
export type GetNotificationSettingsApiArg = void;
export type UpdateNotificationSettingsApiResponse = unknown;
export type UpdateNotificationSettingsApiArg = SettingsNotifications;
export type GetSecuritySettingsApiResponse = /** status 200 OK */ SettingsSecurity;
export type GetSecuritySettingsApiArg = void;
export type UpdateSecuritySettingsApiResponse = unknown;
export type UpdateSecuritySettingsApiArg = SettingsSecurity;
export type GetPlanBillingSettingsApiResponse = /** status 200 OK */ SettingsPlanBilling;
export type GetPlanBillingSettingsApiArg = void;
export type UpdatePlanBillingSettingsApiResponse = unknown;
export type UpdatePlanBillingSettingsApiArg = SettingsPlanBilling;
export type GetUserApplicationsApiResponse = /** status 200 OK */ UserApplication[];
export type GetTeamMembersSettingsApiResponse = /** status 200 OK */ SettingsTeamMember[];
export type GetTeamMembersSettingsApiArg = void;
export type CreateTeamMemberSettingsApiResponse = unknown;
export type CreateTeamMemberSettingsApiArg = SettingsTeamMember;
export type DeleteTeamMemberSettingsApiResponse = unknown;
export type DeleteTeamMemberSettingsApiArg = {
  memberId: string;
};

export type GetGroupsSettingsApiResponse = /** status 200 OK */ SettingsGroup[];
export type GetGroupsSettingsApiArg = void;
export type CreateGroupsSettingsApiResponse = unknown;
export type CreateGroupsSettingsApiArg = SettingsGroup;
export type DeleteGroupsSettingsApiResponse = unknown;
export type DeleteGroupsSettingsApiArg = {
  groupId: string;
};
export type UpdateGroupsSettingsApiResponse = unknown;
export type UpdateGroupsSettingsApiArg = SettingsGroup;

export type GetPermissionsApiResponse = /** status 200 OK */ GroupPermission[];
export type GetPermissionsApiArg = void;

// TypeScript interfaces
interface UploadIconResponse {
  iconUrl: string;
}
//export type SettingsAccount = {
//  id: string;
//  name?: string;
//  username?: string;
//  title?: string;
//  company?: string;
//  about?: string;
//  email?: string;
//  phone?: string;
//  country?: string;
//  language?: string;
//};

export type SettingsAccount = {
    timeZoneId?: string;
    useDeviceTimeZone?: boolean;
    canManageTimeZone?: boolean;
    id: string;
    displayName?: string;
    email?: string;
    title?: string;
    settings?: string;
    shortcuts?: string;
    openingPage?: string | null; // One of OPENING_PAGE_KEYS (configs/openingPageConfig); null = not set
};

export type GetTitleSettingsApiResponse = string;
export type SettingsNotifications = {
  id: string;
  communication?: boolean;
  security?: boolean;
  meetups?: boolean;
  comments?: boolean;
  mention?: boolean;
  follow?: boolean;
  inquiry?: boolean;
};

export type SettingsSecurity = {
  id: string;
  currentPassword?: string;
  newPassword?: string;
  twoStepVerification?: boolean;
  askPasswordChange?: boolean;
  enableAudit?: boolean;
  auditRetentionDays?: number;
  auditRetentionPreset?: string;
  enableBatchRetention?: boolean;
  batchRetentionDays?: number;
  batchRetentionPreset?: string;
};

export type SettingsPlanBilling = {
  id: string;
  plan?: string;
  cardHolder?: string;
  cardNumber?: string;
  cardExpiration?: string;
  cardCVC?: string;
  country?: string;
  zip?: string;
};

// ── Token Usage types ──────────────────────────────────────────────────────────
export type TokenTotals = {
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  costUsd: number;
  turns: number;
  unreportedRecords?: number;
  incompleteRecords?: number;
  unpricedRecords?: number;
};

export type TokenRequestRow = {
  id: string; runId: string | null; model: string; reasoningTier: string | null;
  phase: string | null; routeReason: string | null; effort: string | null;
  inputTokens: number; outputTokens: number; cacheCreationTokens: number;
  cacheCreation1hTokens: number; cacheReadTokens: number; estimatedCostUsd: number;
  costSource: string | null; usageReported: boolean; usageComplete: boolean; costIsKnown: boolean;
};

export type TokenUsageSummary = {
  periodStart: string;
  allowance: {
    monthlyTokenAllowance: number;
    enforcementEnabled: boolean;
    countedThisMonth: number;
    remaining: number;
    percentUsed: number;
  };
  currentPeriod: TokenTotals;
  allTime: TokenTotals;
};

export type TokenUsageSeriesPoint = {
  date: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  costUsd: number;
  turns: number;
  unreportedRecords?: number;
  incompleteRecords?: number;
  unpricedRecords?: number;
};

export type TokenUsageSeries = {
  days: number;
  points: TokenUsageSeriesPoint[];
};

export type TokenUsageBreakdownRow = {
  key: string | null;
  label: string;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  totalTokens: number;
  costUsd: number;
  turns: number;
  unreportedRecords?: number;
  incompleteRecords?: number;
  unpricedRecords?: number;
};

export type TokenUsageBreakdown = {
  by: string;
  days: number;
  rows: TokenUsageBreakdownRow[];
};

export type TokenAllowance = {
  monthlyTokenAllowance: number;
  enforcementEnabled: boolean;
  updatedAt?: string | null;
};

export type TokenAllowanceUpdate = {
  monthlyTokenAllowance: number;
  enforcementEnabled: boolean;
};

export type SettingsTeamMember = {
  id: string;
  avatar?: string;
  name?: string;
  email?: string;
  role?: string;
};

export type SettingsGroup = {
  agentSettings?: GroupAgentSettings | null;
  id: string;
  name: string;
  description: string;
  members: string[]; // Array of member IDs
  permissions: string[]; // Array of permission IDs
  subCategoryPermissions?: string[]; // Array of subcategory permission IDs
};

export type GroupMember = {
  id: string;
  name: string;
  email: string;
  avatar?: string;
};
export type UserApplication = {
  id: string;
  name: string;
  avatar?: string;
  modules: {
    moduleId: string;
    name: string;
  }[];
  trainingStatus?: {
    isTraining: boolean;
    lastTrainedAt?: string;
    progress?: number;
  };
};

export type GroupPermission = {
  id: string;
  name: string;
  description: string;
};

export interface SubPermission {
  id: string;
  name: string;
  description: string;
  groupPermissionId?: string; // Parent permission ID
}

export interface GroupPermissionWithChildren {
  id: string;
  name: string;
  description: string;
  children: SubPermission[];
}

export interface User {
  id: string;
  displayName: string;
  email: string;
  photoURL?: string;
  role?: string;
}

export const {
  useGetAccountTimeZoneQuery,
  useGetAccountSettingsQuery,
  useUpdateAccountSettingsMutation,
  useUploadIconMutation,
  useGetIconQuery,
  useGetTitleQuery,
  
  // User Permissions Hooks
  useGetUserPermissionsQuery,
  useGetSuperAdminStatusQuery,
  useGetGroupedCentralPermissionsQuery,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useGetSecuritySettingsQuery,
  useUpdateSecuritySettingsMutation,
  useChangePasswordMutation,
  useResetTeamMemberPasswordMutation,
  useGetPlanBillingSettingsQuery,
  useUpdatePlanBillingSettingsMutation,
  useGetTeamMembersSettingsQuery,
  useCreateTeamMemberSettingsMutation,
  useDeleteTeamMemberSettingsMutation,
  useUpdateTeamMemberSettingsMutation,
  useGetGroupsSettingsQuery,
  useCreateGroupsSettingsMutation,
  useDeleteGroupsSettingsMutation,
  useUpdateGroupsSettingsMutation,
  useGetPermissionsQuery,
  useGetGroupedPermissionsQuery,
  
  // New hooks for the added endpoints
  useGetGroupPermissionsQuery,
  useAssignGroupPermissionMutation,
  useRemoveGroupPermissionMutation,
  useGetGroupMembersQuery,
  useAddGroupMemberMutation,
  useRemoveGroupMemberMutation,
  useGetSubcategoryPermissionsQuery,
  useGetPermissionSubcategoriesQuery,
  useCreateSubcategoryPermissionMutation,
  useUpdateSubcategoryPermissionMutation,
  useDeleteSubcategoryPermissionMutation,
  useGetGroupSubcategoryPermissionsQuery,
  useAssignGroupSubcategoryPermissionMutation,
  useRemoveGroupSubcategoryPermissionMutation,
  useGetUsersQuery,
  useCreatePermissionMutation,
  useGetUserApplicationsQuery,

  // Token Usage hooks
  useGetTokenUsageSummaryQuery,
  useGetTokenUsageSeriesQuery,
  useGetTokenUsageBreakdownQuery,
  useGetTokenRequestQuery,
  useGetTokenAllowanceQuery,
  useUpdateTokenAllowanceMutation
} = injectedRtkApi;
